import { FC, useEffect, useState } from 'react'
import { useProgrammeExplorerSupporter } from '../ProgrammeExplorerSupporterProvider'
import { ProgrammeExplorerSupporterCard } from './ProgrammeExplorerSupporterCard';
import { ProgrammeExplorerGeneralMetrics } from './ProgrammeExplorerGeneralMetrics';
import './ProgrammeExplorerCard.css'
import CustomModal from 'app/utils/components/modal/modal'
import ProgrammeExplorerHorizontalBarChart from './ProgrammeExplorerHorizontalBarChart'
import ProgrammeExplorerDonutChart from './ProgrammeExplorerDonutChart'
import ProgrammeExplorerGeoChart from './ProgrammeExplorerGeoChart'
import ProgrammeExplorerPieChart from './ProgrammeExplorerPieChart'
import { MetricService } from 'app/services/metric/MetricService'
import { getMetricColors } from 'app/utils/utils'
import { PEBarData, PEDonutData } from '@components/chart/types'
import { metrics } from 'app/utils/data/metrics'

const ProgrammeExplorerBase: FC = () => {
  const ProgrammeExplorer = useProgrammeExplorerSupporter();
  const [wavesOptions, setWaveOptions] = useState<number[] | undefined>(undefined)
  const [showMetrics, setShowMetrics] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [breakdownMetric, setBreakdownMetric] = useState<string>('website_visits');
  const [activeTab, setActiveTab] = useState<'geography' | 'specialty' | 'source' | 'video'>('geography');
  const [geoTopCountries, setGeoTopCountries] = useState<PEBarData[] | null>(null);
  const [geoRegions, setGeoRegions] = useState<PEBarData[] | null>(null);
  const [specialties, setSpecialties] = useState<PEBarData[] | null>(null);
  const [trafficSources, setTrafficSources] = useState<PEDonutData[] | null>(null);
  const [videoAvgPercent, setVideoAvgPercent] = useState<number | null>(null);
  const [totalEngagementNumber, setTotalEngagementNumber] = useState<number | null>(null);
  const [totalEngagementNumberGroupPercent, setTotalEngagementNumberGroupPercent] = useState<{
    social: number | null;
    web: number | null;
  }>({
    social: null,
    web: null,
  });
  const isVideoMetric = ['video_views', 'video_views_podcast_listens', 'social_media_video_views'].includes(breakdownMetric)

  const toggleMetricsShow = () => {
    let metricsShowStatus = false
    const { programmes, campaigns, between_dates, connects } = ProgrammeExplorer.params.filter_filters;
    setWaveOptions(campaigns)
    if ( 
      (programmes !== undefined && programmes.length > 0)
      || (campaigns !== undefined && campaigns.length > 0)
    ) {
      metricsShowStatus = true
    } else if (
      (between_dates !== undefined && between_dates?.from !== undefined && between_dates?.to !== undefined)
    ) {
      metricsShowStatus = true
    } else if (
      (connects !== undefined && connects.length > 0)
    ) {
      metricsShowStatus = true
    }
    setShowMetrics(metricsShowStatus);
  }

  const getProgrammesClassName = (searchValues: string[]) => {
    const filterProgrammes = ProgrammeExplorer.params.filter_filters.programmes;

    // IF the target is project, check if the selected programmes match the search values
  
    if (filterProgrammes && filterProgrammes.length > 0) {    
      
      // Check if any of the selected programmes match the search values
      const anyProgramMatches = filterProgrammes.some(filterId => {
        const foundProgram = ProgrammeExplorer.programmes.find(program => program.id === filterId); // Find the programme by id
        return foundProgram && foundProgram.type ? searchValues.includes(foundProgram.type.toLowerCase()) : false; // Check if the programme type is in the search values
      });
    
      return anyProgramMatches ? '' : 'd-none';
    }

    // If the target is different from project, returns '''.
  
    return '';
  };

  const hasProjectCode = (codes: string[]) => {
    const filters: any = ProgrammeExplorer.params.filter_filters;

    const selectedProgrammeIds: number[] = filters.programmes ?? [];
    if (Array.isArray(selectedProgrammeIds) && selectedProgrammeIds.length > 0) {
      const selectedProgrammes = ProgrammeExplorer.programmes
        .filter(p => selectedProgrammeIds.includes(p.id));

      const matchByProgramme = selectedProgrammes.some(p =>
        codes.includes((p.projectCode ?? '').toString())
      );

      if (matchByProgramme) {
        return true;
      }
    }

    if (filters.project_code_media) {
      return codes.includes(filters.project_code_media.toString());
    }

    return false;
  };

  const hasPublishedDateFrom = (date: string | Date) => {
    const filters: any = ProgrammeExplorer.params.filter_filters;

    const selectedProgrammeIds: number[] = filters.programmes ?? [];

    if (Array.isArray(selectedProgrammeIds) && selectedProgrammeIds.length > 0) {
      const selectedProgrammes = ProgrammeExplorer.programmes
        .filter(p => selectedProgrammeIds.includes(p.id));

      const matchByPublishedDate = selectedProgrammes.some(p =>
        p.publishedAt 
        && 
        new Date(p.publishedAt.date) >= new Date(date)
      );

      if (matchByPublishedDate) {
        return true;
      }
    }

    return false;
  };

  const openDetailsModalForMetric = (metricName: string) => {
    setBreakdownMetric(metricName);
    setActiveTab('geography');
    setShowDetailsModal(true);
  }

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
  }

  useEffect(() => {
    if (!showDetailsModal || !breakdownMetric) return;
    const abortController = new AbortController();
    const loadAll = async () => {
      await Promise.all([
        loadBreakdown(abortController.signal),
        loadTotalEngagementNumber(abortController.signal),
        isVideoMetric ? loadVideoAveragePercent() : Promise.resolve()
      ]);
      if (!isVideoMetric) {
        setVideoAvgPercent(null);
      }
    }
    loadAll();
    return () => {
      abortController.abort();
    }
  }, [showDetailsModal, ProgrammeExplorer.params, breakdownMetric, isVideoMetric]);

  const buildBaseFilters = () => {
    let { programmes, campaigns } = ProgrammeExplorer.params.filter_filters;
    if (campaigns !== undefined && campaigns.length > 0) {
      programmes = [];
    }
    return {
      ...ProgrammeExplorer.params,
      filter_filters: {
        ...ProgrammeExplorer.params.filter_filters,
        programmes,
        therapeutic_areas: []
      }
    };
  }

  const loadBreakdown = async (signal: AbortSignal) => {
    try {
      const modifiedFilters: any = {
        ...buildBaseFilters()
      };
      const response = (await (new MetricService()).getMetricEndpointData(modifiedFilters, breakdownMetric, 'breakdown', signal)).getResponseData();
      if (!response || !response.success) {
        setGeoTopCountries([]);
        setGeoRegions([]);
        setSpecialties([]);
        setTrafficSources([]);
        return;
      }
      const payload = response.data || {};
      const regions: any[] = payload.region || [];
      const specialtiesArr: any[] = payload['terapeutic-area'] || payload['therapeutic_area'] || [];
      const sourcesArr: any[] = payload.source || [];

      const totalRegion = regions.reduce((sum: number, item: any) => sum + (item.total ?? item.value ?? 0), 0);
      const regionOrdered = regions
        .map((item: any) => ({
          name: item.group || item.name || 'unknown',
          total: item.total ?? item.value ?? 0
        }))
        .sort((a: any, b: any) => (b.total || 0) - (a.total || 0));
      const regionBar: PEBarData[] = regionOrdered.map((item: any) => ({
        name: item.name,
        color: getMetricColors(0),
        data: totalRegion > 0 ? Math.round((item.total / totalRegion) * 100) : 0
      }));
      setGeoRegions(regionBar);

      const allCountries: { name: string; total: number }[] = regions.flatMap((r: any) =>
        ((r.country || r.countries || []) as any[]).map((c: any) => ({
          name: c.group || c.name || 'unknown',
          total: c.total ?? c.value ?? 0
        }))
      );
      const totalCountries = allCountries.reduce((sum: number, item: any) => sum + (item.total || 0), 0);
      const aggregatedMap = new Map<string, number>();
      allCountries.forEach((c) => {
        const key = c.name;
        const prev = aggregatedMap.get(key) || 0;
        aggregatedMap.set(key, prev + (c.total || 0));
      });
      const aggregatedCountries = Array.from(aggregatedMap.entries()).map(([name, total]) => ({ name, total }));
      const topCountries = aggregatedCountries
        .sort((a: any, b: any) => (b.total || 0) - (a.total || 0))
        .slice(0, 10);
      const countriesBar: PEBarData[] = topCountries.map((item: any) => ({
        name: item.name,
        color: getMetricColors(0),
        data: totalCountries > 0 ? Math.round((item.total / totalCountries) * 100) : 0
      }));
      setGeoTopCountries(countriesBar);

      // Specialties (%)
      const totalSpecialties = specialtiesArr.reduce((sum: number, item: any) => sum + (item.total ?? item.value ?? 0), 0);
      const specialtiesOrdered = specialtiesArr
        .map((item: any) => ({
          name: item.group || item.name || 'unknown',
          total: item.total ?? item.value ?? 0
        }))
        .sort((a: any, b: any) => (b.total || 0) - (a.total || 0))
        .slice(0, 5);
      const specialtiesBar: PEBarData[] = specialtiesOrdered.map((item: any, idx: number) => ({
        name: item.name,
        color: getMetricColors(idx),
        data: totalSpecialties > 0 ? Math.round((item.total / totalSpecialties) * 100) : 0
      }));
      setSpecialties(specialtiesBar);

      // Sources (%)
      const normalizedSources = sourcesArr.map((item: any) => ({
        name: (item.group || item.name || 'unknown') as string,
        total: item.total ?? item.value ?? 0
      }))
      // Agrupar en las tres categorías solicitadas
      const groupTotals: Record<string, number> = { 'Social Media': 0, 'Newsletter': 0, 'Search': 0 }
      normalizedSources.forEach((s: any) => {
        const n = (s.name || '').toLowerCase()
        if (n.includes('social')) {
          groupTotals['Social Media'] += s.total || 0
        } else if (n.includes('newsletter') || n.includes('news') || n.includes('email')) {
          groupTotals['Newsletter'] += s.total || 0
        } else if (n.includes('search') || n.includes('organic')) {
          groupTotals['Search'] += s.total || 0
        }
      })
      const totalSources = Object.values(groupTotals).reduce((sum, v) => sum + v, 0)
      const sourcesThree: PEDonutData[] = ['Social Media', 'Newsletter', 'Search'].map((name, idx) => ({
        name,
        color: getMetricColors(idx),
        data: totalSources > 0 ? Math.round(((groupTotals[name] || 0) / totalSources) * 100) : 0
      }))
      setTrafficSources(sourcesThree);
    } catch (e) {
      setGeoTopCountries([]);
      setGeoRegions([]);
      setSpecialties([]);
      setTrafficSources([]);
    }
  }

  const loadVideoAveragePercent = async () => {
    const response = (await (new MetricService()).getMetricData(buildBaseFilters(), metrics.avgVideoWatchedVimeo.endpoint)).getResponseData();
    if (!response || !response.success) {
      setVideoAvgPercent(null);
      return;
    }
    const values = response.data;
    if (Array.isArray(values) && values.length > 0 && values[0]?.value !== undefined) {
      setVideoAvgPercent(Math.round(values[0].value));
    } else if (typeof values === 'number') {
      setVideoAvgPercent(Math.round(values));
    } else {
      setVideoAvgPercent(null);
    }
  }

  const loadTotalEngagementNumber = async (signal: AbortSignal) => {
    try {
      const response = (await (new MetricService()).getMetricEndpointData(buildBaseFilters(), 'engagement_total', 'total', signal)).getResponseData();
      if (!response || !response.success) {
        setTotalEngagementNumber(null);
        return;
      }
      const data = response.data;
      if (typeof data === 'number') {
        setTotalEngagementNumber(Math.round(data));
      } else if (Array.isArray(data) && data.length > 0 && typeof data[0].value === 'number') {
        setTotalEngagementNumber(Math.round(data[0].value));
      } else {
        setTotalEngagementNumber(Number(data) || null);
      }
    } catch (error) {
      setTotalEngagementNumber(null);
    }
  }

  const loadTotalEngagementNumberGroupPercent = async (signal: AbortSignal) => {
    try {
      const baseFilters = buildBaseFilters();

      const filtersWithPercentage: any = {
        ...baseFilters,
        filter_filters: {
          ...baseFilters.filter_filters,
          aggregation: 'percentage',
        },
      };

      const response = (
        await new MetricService().getMetricEndpointData(
          filtersWithPercentage,
          'engagement_total',
          'group',
          signal
        )
      ).getResponseData();

      if (!response || !response.success || !Array.isArray(response.data)) {
        setTotalEngagementNumberGroupPercent({ social: null, web: null });
        return;
      }

      const social = response.data.find((item: any) => item.group === 'social');
      const web = response.data.find((item: any) => item.group === 'web');

      setTotalEngagementNumberGroupPercent({
        social: social?.total ?? null,
        web: web?.total ?? null,
      });
    } catch {
      setTotalEngagementNumberGroupPercent({ social: null, web: null });
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    loadTotalEngagementNumberGroupPercent(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [ProgrammeExplorer.params.filter_filters]);

  useEffect(() => {
    toggleMetricsShow();
    console.log("Filter has changed", ProgrammeExplorer);
  }, [ProgrammeExplorer.params.filter_filters])

  const totalEngagementNumberGroupPercentSocial = totalEngagementNumberGroupPercent.social;
  const totalEngagementNumberGroupPercentWeb = totalEngagementNumberGroupPercent.web;

  return (
    <>
      {
        !showMetrics ?
          (<h3 className={'text-muted text-center'}>You must select a programme to view data</h3>)
          : (
            <><div className='row'>
              <div className='col-xl-12'>
                <div className='title-supporter'>Impact (Moore's levels*)</div>
                <div className='subtitle-supporter'>What do HCPs think about the programme?<span className='sign-title'> *</span></div>
                <div className='cardcmewrap'>
                  <ProgrammeExplorerSupporterCard
                    key={'1'}
                    className='card cardcme programme-explorer-card-orange height-impact '
                    image='/media/svg/programme_explorer_supporter/medical-stethoscope-variant.svg'
                    title=''
                    level={'4'}
                    filters={ProgrammeExplorer.params}
                    metrics={[{ humanName: 'of HCPs agree they intend to change ther clinical practice as a result of this programme', name: 'practice_changing'}]}
                    symbol='%'
                    cardColor='orange'
                    option='impact'
                    wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                    onClick={() => openDetailsModalForMetric('practice_changing')}
                  />


                  <ProgrammeExplorerSupporterCard
                    key={'2'}
                    className='card cardcme height-impact'
                    image="/media/svg/programme_explorer_supporter/diamond.svg"
                    title=''
                    level={'2'}
                    filters={ProgrammeExplorer.params}
                    metrics={[{ humanName: 'of HCPs agree this programme is valuable', name: 'valuable'}]}
                    symbol='%'
                    cardColor='white'
                    option='impact'
                    wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                    onClick={() => openDetailsModalForMetric('valuable')}
                  />

                  <ProgrammeExplorerSupporterCard
                    key={'3'}
                    className='card cardcme height-impact'
                    image="/media/svg/programme_explorer_supporter/idea.svg"
                    title=''
                    level={'3A'}
                    filters={ProgrammeExplorer.params}
                    metrics={[{ humanName: 'of HCPs agree this programme has improved their knowledge', name: 'knowledge_improving' }]}
                    symbol='%'
                    cardColor='white'
                    option='impact'
                    wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                    onClick={() => openDetailsModalForMetric('knowledge_improving')}
                  />


                  <ProgrammeExplorerSupporterCard
                    key={'4'}
                    className='card cardcme height-impact'
                    image="/media/svg/programme_explorer_supporter/ethics.svg"
                    title=''
                    level={''}
                    filters={ProgrammeExplorer.params}
                    metrics={[{ humanName: 'of HCPs agree this programme is balanced and unbiased', name: 'balanced_and_unbiased' }]}
                    symbol='%'
                    cardColor='white'
                    option='impact'
                    wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                    onClick={() => openDetailsModalForMetric('balanced_and_unbiased')}
                  />

                  {/*
                    <ProgrammeExplorerSupporterCard
                      key={'5'}
                      className='card cardcme height-impact'
                      image="/media/svg/programme_explorer_supporter/doctor.svg"
                      title=''
                      level={'5'}
                      filters={ProgrammeExplorer.params}
                      metrics={[{ humanName: 'of HCPs agree this programme improved their performance', name: 'performance_improving' }]}
                      symbol=''
                      cardColor='white'
                      option='impact'
                      wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                      onClick={openDetailsModal}
                    />
                  */}
                </div>
              </div>

              {/*<div className='col-xl-3'>
                <div className='title-supporter'>Reach</div>
                <div className='subtitle-supporter'>How many HCPs in our community received the programme?</div>
                <div className='cardcmewrap'>
                  <ProgrammeExplorerSupporterCard
                    key={'6'}
                    className='card cardcme'
                    image='/media/svg/programme_explorer_supporter/laptop.svg'
                    title='Newsletter'
                    level={''}
                    filters={ProgrammeExplorer.params}
                    metrics={[{ humanName: 'No. of HCPs Reached', name: 'hpcs_reached_out_total' }]}
                    symbol=''
                    cardColor='white'
                    onClick={() => openDetailsModalForMetric('hpcs_reached_out_total')} />
                </div>
              </div>*/}
            </div><div className='row'>
                <div className='col-xl-12'>
                  <div className='title-supporter'>Engagement</div>
                  <div className='subtitle-supporter'>How many HCPs engaged with the programme?</div>
                  <div className='cardcmewrap'>
                    {(
                      hasProjectCode(['AZ 24-09-01n', 'ME 25-03-03l', 'IP 24-08-02j', 'C2 25-01-05l']) 
                      ||
                      hasPublishedDateFrom('2026-08-12')
                    ) ? (
                      <ProgrammeExplorerSupporterCard
                        key={'7'}
                        className='card cardcme programme-explorer-card-orange'
                        image='/media/svg/programme_explorer_supporter/tablero.svg'
                        title=''
                        level={''}
                        filters={ProgrammeExplorer.params}
                        metrics={[{ humanName: 'Total Engagement Number', name: 'engagement_total_v2' }]}
                        symbol=''
                        cardColor='orange'
                        onClick={() => openDetailsModalForMetric('engagement_total_v2')} />
                    ) : (
                      <ProgrammeExplorerSupporterCard
                        key={'7'}
                        className='card cardcme programme-explorer-card-orange'
                        image='/media/svg/programme_explorer_supporter/tablero.svg'
                        title=''
                        level={''}
                        filters={ProgrammeExplorer.params}
                        metrics={[{ humanName: 'Total Engagement Number', name: 'engagement_total' }]}
                        symbol=''
                        cardColor='orange'
                        onClick={() => openDetailsModalForMetric('engagement_total')} />
                    )}

                    <ProgrammeExplorerSupporterCard
                      key={'8'}
                      className='card cardcme'
                      image='/media/svg/programme_explorer_supporter/download.svg'
                      title=''
                      level={''}
                      filters={ProgrammeExplorer.params}
                      metrics={[{ humanName: 'Total Number of Downloads', name: 'slide_downloads' }]}
                      symbol=''
                      cardColor='white'
                      onClick={() => openDetailsModalForMetric('slide_downloads')} />

                    {(
                      hasProjectCode(['AZ 24-09-01n', 'ME 25-03-03l', 'IP 24-08-02j', 'C2 25-01-05l']) 
                      ||
                      hasPublishedDateFrom('2026-08-12')
                    ) ? (
                      <ProgrammeExplorerSupporterCard
                        key={'9'}
                        className='card cardcme'
                        image='/media/svg/programme_explorer_supporter/video-camera.svg'
                        title=''
                        level={''}
                        filters={ProgrammeExplorer.params}
                        metrics={[
                          { humanName: ' Total Video Views / Podcast Listens', name:  'video_views_podcast_listens' }
                        ]}
                        symbol=''
                        cardColor='white'
                        onClick={() => openDetailsModalForMetric('video_views_podcast_listens')} />
                    ) : (
                      <>
                        <ProgrammeExplorerSupporterCard
                          key={'9'}
                          className='card cardcme'
                          image='/media/svg/programme_explorer_supporter/video-camera.svg'
                          title=''
                          level={''}
                          filters={ProgrammeExplorer.params}
                          metrics={[
                            { humanName: ' Total Video Views', name:  'video_views' }
                          ]}
                          symbol=''
                          cardColor='white'
                          onClick={() => openDetailsModalForMetric('video_views')} />

                        <ProgrammeExplorerSupporterCard
                          key={'10'}
                          className={`card cardcme ${getProgrammesClassName(["podcast"])}`}
                          image='/media/svg/programme_explorer_supporter/mic.svg'
                          title=''
                          level={''}
                          filters={ProgrammeExplorer.params}
                          metrics={[{ humanName: 'Total Number of Podcast Listens', name: 'podcast_listens', type: 'podcast' }]}
                          symbol=''
                          cardColor='white'
                          wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                          onClick={() => openDetailsModalForMetric('podcast_listens')} />
                      </>
                    )}

                    <ProgrammeExplorerSupporterCard
                      key={'12'}
                      className={`card cardcme ${getProgrammesClassName(["micro-learning", "e-learning"])}`}
                      image='/media/svg/programme_explorer_supporter/open-book.svg'
                      title=''
                      level={''}
                      filters={ProgrammeExplorer.params}
                      metrics={[{ humanName: 'Total Number of E-learning Visits', name: 'website_visits', type: 'elearning' }]}
                      symbol=''
                      cardColor='white'
                      elearning={ProgrammeExplorer.elearning}
                      onClick={() => openDetailsModalForMetric('website_visits')}
                      />
                  </div>
                </div>
                <div className='col-xl-12'>
                  <div className='title-supporter'>Social Media</div>
                  <div className='subtitle-supporter'>How many people interacted with the programme on social media?</div>
                  <div className='cardcmewrap'>
                      <ProgrammeExplorerSupporterCard
                        key={'13'}
                        className='card cardcme'
                        image='/media/svg/programme_explorer_supporter/video-camera.svg'
                        title=''
                        level={''}
                        filters={ProgrammeExplorer.params}
                        metrics={[
                          { humanName: 'Total Video Views on Social Media', name: 'social_media_video_views' },
                        ]}
                        symbol=''
                        cardColor='white'
                        onClick={() => openDetailsModalForMetric('social_media_video_views')} />

                      <ProgrammeExplorerSupporterCard
                        key={'14'}
                        className='card cardcme'
                        image='/media/svg/programme_explorer_supporter/heart.svg'
                        title=''
                        level={''}
                        filters={ProgrammeExplorer.params}
                        metrics={[{ humanName: 'Total Social Media Engagements', name: 'social_media_engagements' }]}
                        symbol=''
                        cardColor='white'
                        wave={wavesOptions && wavesOptions?.length > 0 ? false : true}
                        onClick={() => openDetailsModalForMetric('social_media_engagements')} />
                  </div>
                  </div>
                  <div className='col-xl-12 mt-8'>
                  <div className='title-supporter'>Detailed Demographics & Sources</div>
                  <div className='subtitle-supporter'>Breakdown of traffic, types and geography</div>
                    <ProgrammeExplorerGeneralMetrics />
                  </div>
                  </div>
                  <CustomModal
                    identifier='programme-explorer-supporter-details'
                    show={showDetailsModal}
                    position='center'
                    onHideModal={closeDetailsModal}
                    dialogStyle={{ maxWidth: 520 }}
                  >
                  <div className='modal-header justify-content-center border-0 pb-0'>
                    <h4 className='modal-title text-center fw-bold'>
                      Total Engagement Number Split
                    </h4>
                  </div>

                  <div className='modal-body'>
                    <div
                      className='d-flex justify-content-center gap-4'
                      style={{ flexWrap: 'wrap' }}
                    >
                    {/* Website */}
                    <div
                      style={{
                        width: 200,
                        height: 219,
                        maxWidth: 200,
                        background: 'rgb(198, 87, 59)',
                        borderRadius: 17,
                        boxShadow: '0 0 8px rgba(0,0,0,.16)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        padding: 28,
                        position: 'relative',
                        color: '#fff',
                        fontFamily: 'Poppins, Helvetica, sans-serif',
                      }}
                    >
                    <div
                      style={{
                        width: 50,
                        height: 50,
                        background: '#fff',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src='/media/svg/programme_explorer_supporter/website.svg'
                        alt='Website'
                        width={45}
                        height={45}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        fontSize: 22,
                        fontWeight: 400,
                        lineHeight: 1.2,
                        color: '#fff',
                      }}
                    >
                      {totalEngagementNumberGroupPercentWeb ?? '-'}%
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 16,
                        color: '#fff',
                      }}
                    >
                      From Website/Rest
                    </div>
                  </div>

                  {/* Social */}
                  <div
                    style={{
                      width: 200,
                      height: 219,
                      maxWidth: 200,
                      background: 'rgb(198, 87, 59)',
                      borderRadius: 17,
                      boxShadow: '0 0 8px rgba(0,0,0,.16)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      padding: 28,
                      position: 'relative',
                      color: '#fff',
                      fontFamily: 'Poppins, Helvetica, sans-serif',
                    }}
                  >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      background: '#fff',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                  <img
                    src='/media/svg/programme_explorer_supporter/heart.svg'
                    alt='Social Media'
                    width={45}
                    height={45}
                  />
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      fontSize: 22,
                      fontWeight: 400,
                      lineHeight: 1.2,
                      color: '#fff',
                    }}
                  >
                    {totalEngagementNumberGroupPercentSocial ?? '-'}%
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 16,

                      color: '#fff',
                    }}
                  >
                    From Social Media
                  </div>
                  </div>
                  </div>
                  </div>
                </CustomModal>
            </>
          )
      }
    </>
  )
}

export { ProgrammeExplorerBase }
